import React, { useState, useEffect } from 'react';
import { Globe, X } from 'lucide-react';
import { Country } from '../../types/sports';

interface CountryFormProps {
  country?: Country | null;
  onSave: (data: {
    name: string;
    code: string;
    alpha2Code?: string;
    flag?: string;
    isActive?: boolean;
  }) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

const CountryForm: React.FC<CountryFormProps> = ({
  country,
  onSave,
  onCancel,
  loading = false
}) => {
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    alpha2Code: '',
    flag: '',
    isActive: true
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (country) {
      setFormData({
        name: country.name,
        code: country.code,
        alpha2Code: country.alpha2Code || '',
        flag: country.flag || '',
        isActive: country.isActive
      });
    } else {
      setFormData({
        name: '',
        code: '',
        alpha2Code: '',
        flag: '',
        isActive: true
      });
    }
  }, [country]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name ist erforderlich';
    }

    if (!formData.code.trim()) {
      newErrors.code = 'Code ist erforderlich';
    } else if (formData.code.length !== 3) {
      newErrors.code = 'Code muss genau 3 Zeichen lang sein (ISO 3166-1 alpha-3)';
    }

    if (formData.alpha2Code && formData.alpha2Code.length !== 2) {
      newErrors.alpha2Code = 'Alpha-2 Code muss genau 2 Zeichen lang sein';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      const submitData = {
        name: formData.name.trim(),
        code: formData.code.trim().toUpperCase(),
        alpha2Code: formData.alpha2Code.trim().toUpperCase() || undefined,
        flag: formData.flag.trim() || undefined,
        ...(country && { isActive: formData.isActive })
      };

      await onSave(submitData);
    } catch (error) {
      console.error('Error saving country:', error);
    }
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        <div style={headerStyle}>
          <h2 style={titleStyle}>
            {country ? <><Globe size={16} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} /> Land bearbeiten</> : <><Globe size={16} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} /> Neues Land erstellen</>}
          </h2>
          <button
            onClick={onCancel}
            style={closeButtonStyle}
            disabled={loading}
          >
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={formStyle}>
          <div style={fieldGroupStyle}>
            <label style={labelStyle}>
              Name *
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                style={{
                  ...inputStyle,
                  ...(errors.name ? errorInputStyle : {})
                }}
                placeholder="z.B. Deutschland"
                disabled={loading}
                maxLength={100}
              />
              {errors.name && <span style={errorTextStyle}>{errors.name}</span>}
            </label>
          </div>

          <div style={fieldGroupStyle}>
            <label style={labelStyle}>
              Code (ISO 3166-1 alpha-3) *
              <input
                type="text"
                value={formData.code}
                onChange={(e) => handleInputChange('code', e.target.value.toUpperCase())}
                style={{
                  ...inputStyle,
                  ...(errors.code ? errorInputStyle : {})
                }}
                placeholder="z.B. DEU"
                disabled={loading}
                maxLength={3}
              />
              {errors.code && <span style={errorTextStyle}>{errors.code}</span>}
            </label>
          </div>

          <div style={fieldGroupStyle}>
            <label style={labelStyle}>
              Alpha-2 Code (ISO 3166-1 alpha-2)
              <input
                type="text"
                value={formData.alpha2Code}
                onChange={(e) => handleInputChange('alpha2Code', e.target.value.toUpperCase())}
                style={{
                  ...inputStyle,
                  ...(errors.alpha2Code ? errorInputStyle : {})
                }}
                placeholder="z.B. DE"
                disabled={loading}
                maxLength={2}
              />
              {errors.alpha2Code && <span style={errorTextStyle}>{errors.alpha2Code}</span>}
            </label>
          </div>

          <div style={fieldGroupStyle}>
            <label style={labelStyle}>
              Flagge (Emoji)
              <input
                type="text"
                value={formData.flag}
                onChange={(e) => handleInputChange('flag', e.target.value)}
                style={inputStyle}
                placeholder="z.B. 🇩🇪"
                disabled={loading}
                maxLength={10}
              />
            </label>
          </div>

          {country && (
            <div style={fieldGroupStyle}>
              <label style={checkboxLabelStyle}>
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => handleInputChange('isActive', e.target.checked)}
                  style={checkboxStyle}
                  disabled={loading}
                />
                Aktiv
              </label>
            </div>
          )}

          <div style={buttonGroupStyle}>
            <button
              type="button"
              onClick={onCancel}
              style={cancelButtonStyle}
              disabled={loading}
            >
              Abbrechen
            </button>
            <button
              type="submit"
              style={{
                ...saveButtonStyle,
                ...(loading ? disabledButtonStyle : {})
              }}
              disabled={loading}
            >
              {loading ? 'Speichern...' : 'Speichern'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Styles
const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};

const modalStyle: React.CSSProperties = {
  backgroundColor: 'white',
  borderRadius: '8px',
  padding: '0',
  width: '90%',
  maxWidth: '500px',
  maxHeight: '90vh',
  overflow: 'auto',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '1.5rem',
  borderBottom: '1px solid #e5e7eb',
};

const titleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: '1.5rem',
  fontWeight: 'bold',
  color: '#1f2937',
};

const closeButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  fontSize: '1.5rem',
  cursor: 'pointer',
  color: '#6b7280',
  padding: '0.25rem',
  borderRadius: '4px',
};

const formStyle: React.CSSProperties = {
  padding: '1.5rem',
};

const fieldGroupStyle: React.CSSProperties = {
  marginBottom: '1.5rem',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.875rem',
  fontWeight: '500',
  color: '#374151',
  marginBottom: '0.5rem',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.75rem',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  fontSize: '1rem',
  marginTop: '0.25rem',
  boxSizing: 'border-box',
};

const errorInputStyle: React.CSSProperties = {
  borderColor: '#ef4444',
};

const errorTextStyle: React.CSSProperties = {
  color: '#ef4444',
  fontSize: '0.75rem',
  marginTop: '0.25rem',
  display: 'block',
};

const checkboxLabelStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  fontSize: '0.875rem',
  fontWeight: '500',
  color: '#374151',
  cursor: 'pointer',
};

const checkboxStyle: React.CSSProperties = {
  marginRight: '0.5rem',
  width: '1rem',
  height: '1rem',
};

const buttonGroupStyle: React.CSSProperties = {
  display: 'flex',
  gap: '0.75rem',
  justifyContent: 'flex-end',
  marginTop: '2rem',
};

const cancelButtonStyle: React.CSSProperties = {
  padding: '0.75rem 1.5rem',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  backgroundColor: 'white',
  color: '#374151',
  cursor: 'pointer',
  fontSize: '0.875rem',
  fontWeight: '500',
};

const saveButtonStyle: React.CSSProperties = {
  padding: '0.75rem 1.5rem',
  border: 'none',
  borderRadius: '6px',
  backgroundColor: '#3b82f6',
  color: 'white',
  cursor: 'pointer',
  fontSize: '0.875rem',
  fontWeight: '500',
};

const disabledButtonStyle: React.CSSProperties = {
  opacity: 0.6,
  cursor: 'not-allowed',
};

export default CountryForm;
